import React__default from 'react';
import { getNextIndexFromKey } from '../../../utils/hooks/useListKeyboardNavigation.js';
import { sanitizeRowProps } from '../util.js';
import { useControllableState } from '@radix-ui/react-use-controllable-state';

const useTableKeyboardNavigation = (props, rows, rowProps, ref) => {
  const useGlobalKeyboardNavigation = props.dangerouslyHijackGlobalKeyboardNavigation;
  const [activeIndex, setActiveIndex] = useControllableState({
    prop: props.activeIndex,
    defaultProp: props.defaultActiveIndex !== undefined ? props.defaultActiveIndex : useGlobalKeyboardNavigation ? 0 : undefined,
    onChange: index => {
      if (index !== undefined) {
        var _props$onChangeActive;

        (_props$onChangeActive = props.onChangeActiveIndex) === null || _props$onChangeActive === void 0 ? void 0 : _props$onChangeActive.call(props, index);
      }
    }
  });

  const onKeyDown = event => {
    var _document$activeEleme;

    const isModifierKeyPressed = event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;

    if (useGlobalKeyboardNavigation && document.activeElement !== ref.current && ((_document$activeEleme = document.activeElement) === null || _document$activeEleme === void 0 ? void 0 : _document$activeEleme.getAttribute('type')) !== 'search' && document.activeElement !== document.body) {
      return;
    } // abort key handling if other elements inside table are focused and we don't use global keyboard navigation


    if (!useGlobalKeyboardNavigation && document.activeElement !== ref.current) {
      return;
    }

    if (activeIndex !== undefined) {
      const currentRow = rows[activeIndex];

      if (currentRow) {
        const sanitizedRow = sanitizeRowProps(currentRow, rowProps.rowExpansionRenderer);

        if (rowProps.onRowClick && event.key === 'Enter') {
          event.preventDefault();
          rowProps.onRowClick(sanitizedRow);
          return;
        }

        if (currentRow.toggleRowSelected && event.key === ' ') {
          event.preventDefault();
          currentRow.toggleRowSelected();
          return;
        }

        if (currentRow.toggleRowExpanded) {
          if (currentRow.isExpanded && event.key === 'ArrowLeft') {
            event.preventDefault();
            currentRow.toggleRowExpanded();
            return;
          } else if (!currentRow.isExpanded && event.key === 'ArrowRight') {
            event.preventDefault();
            currentRow.toggleRowExpanded();
            return;
          }
        } // inline editing


        if (currentRow.toggleRowEditing) {
          if (currentRow.canEdit && !currentRow.isEditing) {
            if (rowProps.onRowCreate && event.shiftKey && event.key === 'n') {
              event.preventDefault();

              if (!currentRow.isExpanded) {
                currentRow.toggleRowExpanded();
              }

              rowProps.onRowCreate(sanitizedRow, event);
              return;
            }

            if (event.key === 'e') {
              event.preventDefault();
              currentRow.toggleRowEditing();
              return;
            }
          }
        }

        if (rowProps.onRowEdit && event.key === 'e' && !isModifierKeyPressed) {
          event.preventDefault();
          rowProps.onRowEdit(sanitizedRow, event);
          return;
        }

        if (rowProps.onRowCopy && event.key === 'c' && !isModifierKeyPressed) {
          event.preventDefault();
          rowProps.onRowCopy(sanitizedRow, event);
          return;
        }

        if (rowProps.onRowDelete && event.key === 'Delete' && !isModifierKeyPressed) {
          event.preventDefault();
          rowProps.onRowDelete(sanitizedRow, event);
          return;
        }
      }
    }

    const nextIndex = getNextIndexFromKey(event.key, rows.length, activeIndex);

    if (nextIndex !== undefined) {
      event.preventDefault();
      setActiveIndex(nextIndex);
    }
  };

  const handleKeyDown = event => {
    if (!useGlobalKeyboardNavigation) {
      onKeyDown(event.nativeEvent);
    }
  };

  React__default.useEffect(() => {
    if (useGlobalKeyboardNavigation) {
      window.addEventListener('keydown', onKeyDown);
    }

    return () => {
      if (useGlobalKeyboardNavigation) {
        window.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [onKeyDown]);

  const handleFocus = () => {
    if (activeIndex === undefined && rows.length) {
      setActiveIndex(0);
    }
  };

  return [activeIndex, setActiveIndex, handleKeyDown, handleFocus];
};

export { useTableKeyboardNavigation };
//# sourceMappingURL=useTableKeyboardNavigation.js.map
